'use client';

import { useCallback, useMemo } from 'react';
import Big from 'big.js';

import type {
  BalanceSource,
  CompactBalance,
  Quantity,
} from '@personly/libs-types';
import {
  RawQuantity,
  measuresEqual,
  sortQuantities,
} from '@personly/libs-quantities';

export type Balance = {
  quantitiesList: Array<RawQuantity>;
  typeHandle: string;
  source?: BalanceSource;
};

const useBalanceSources = (balances: Balance[]) => {
  const compactBalances = useCallback(
    (typeHandle?: string, balancePermissions = {}) => {
      const data = balances
        .filter((balance) =>
          typeHandle ? balance.typeHandle === typeHandle : true
        )
        .filter((balance) =>
          Object.keys(balancePermissions).length
            ? balance.source!.ref!.typeHandle in balancePermissions
            : true
        )
        .reduce((prev: Array<CompactBalance>, next) => {
          for (const quantity of next.quantitiesList) {
            const i = prev.findIndex(
              (balanceSource) =>
                measuresEqual(balanceSource.measure, quantity.measure) &&
                balanceSource.typeHandle === next.source!.ref!.typeHandle
            );
            if (i === -1) {
              prev.push({
                typeHandle: next.source!.ref!.typeHandle,
                measure: quantity.measure,
                value: Big(quantity.value),
              });
            } else {
              prev[i].value = prev[i].value.plus(quantity.value);
            }
          }

          return prev;
        }, [])
        .sort(sortQuantities);

      return data;
    },
    [balances]
  );

  const compactBalanceTotals = useCallback(
    (typeHandle?: string, balancePermissions = {}) =>
      balances
        .filter((balance) =>
          typeHandle ? balance.typeHandle === typeHandle : true
        )
        .filter((balance) =>
          Object.keys(balancePermissions).length
            ? balance.source!.ref!.typeHandle in balancePermissions
            : true
        )
        .reduce<Quantity[]>((acc, cur) => {
          for (const quantity of cur.quantitiesList) {
            const i = acc.findIndex((a) =>
              measuresEqual(a.measure, quantity.measure)
            );

            if (i === -1)
              acc.push({
                measure: quantity.measure,
                value: Big(quantity.value),
              });
            else acc[i].value = acc[i].value.plus(quantity.value);
          }

          return acc;
        }, [])
        .sort(sortQuantities),
    [balances]
  );

  const result = useMemo(
    () => ({ compactBalances, compactBalanceTotals }),
    [compactBalances, compactBalanceTotals]
  );

  return result;
};

export default useBalanceSources;
